import { Button, FormRow, FormStatus } from '@dnb/eufemia';
import { HelpProps } from '@dnb/eufemia/components/help-button/HelpButtonInline';
import { Form } from '@dnb/eufemia/extensions/forms';
import type { ClientDto } from '@portals/shared/portal/ClientDto';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import { type JSX, useCallback, useMemo, useState } from 'react';

import { deleteClient } from '@/api/app';
import LoadingModal from '@/components/LoadingModal';
import Toast from '@/components/Toast';

import { CrqField } from './CrqField';

export type ClientProps = {
  isAdmin: boolean;
  client: ClientDto;
  onDeleteClicked?: () => void;
  hasPrivilegeToEditClient: boolean;
  noPrivilegeError: React.ReactNode;
};

type FormSchemaJson = {
  crq: string | undefined;
};

const crqHelp: HelpProps = {
  title: 'ServiceNow Identifier (BA/BSN)',
  content:
    'A ServiceNow change request (CRQ) in the "implement" state is required to delete a client in the live environment. Enter the ID for the relevant CRQ.',
};

export default function ClientDeleteDrawer({
  isAdmin,
  client,
  onDeleteClicked,
  hasPrivilegeToEditClient,
  noPrivilegeError,
}: ClientProps): JSX.Element | null {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<null | string>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = useCallback(
    async ({ crq }: FormSchemaJson) => {
      try {
        setSubmitting(true);
        await deleteClient(client.appId, client.id, crq);
        setErrorMessage(null);
        setSuccessMessage('Client deleted successfully');
        if (onDeleteClicked) {
          onDeleteClicked();
        }
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
        return;
      } catch (error: unknown) {
        setSubmitting(false);
        if (ApiError.isApiError(error)) {
          setErrorMessage(error.body.message);
        } else {
          throw error;
        }
      }
    },
    [client, onDeleteClicked],
  );

  const deleteDisabled = useMemo(() => {
    return !isAdmin || !hasPrivilegeToEditClient;
  }, [isAdmin, hasPrivilegeToEditClient]);

  return (
    <Form.Handler<FormSchemaJson> data={{ crq: undefined }} onSubmit={onSubmit}>
      {submitting && <LoadingModal />}
      <FormStatus bottom="medium" state="warn" top="medium">
        Are you sure to delete this client? This action cannot be undone.
      </FormStatus>

      {successMessage && <Toast message={successMessage} position="bottom" variant="success" />}
      {isAdmin && client.liveMode && <CrqField customHelp={crqHelp} />}
      {errorMessage && (
        <FormRow>
          <FormStatus bottom="large" text={errorMessage} top="medium" />
        </FormRow>
      )}
      <FormRow>
        <Button
          disabled={deleteDisabled}
          left="small"
          size="large"
          text="Delete client"
          tooltip={deleteDisabled ? noPrivilegeError : undefined}
          top="small"
          type="submit"
        />
      </FormRow>
    </Form.Handler>
  );
}
